/* eslint-disable max-lines-per-function */

import {
   MedicalInformationType,
   PregnancyCompletionType,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { ModalConfirmButton } from '@dreryk/react-components';

import { patientMedicalInformationTranslationKeys } from '../../translations';
import { ChronicDiseaseApproveDraftAction } from '../chronicDisease/entryForm/ChronicDiseaseApproveDraftAction';
import { ChronicDiseaseEntryForm } from '../chronicDisease/entryForm/ChronicDiseaseEntryForm';
import { GynecologicalInterviewActionsRenderer } from '../gynecologicalInterview/GynecologicalInterviewActionsRenderer';
import { GynecologicalInterviewAddEntryForm } from '../gynecologicalInterview/gynecologicalInterviewAddEntryForm/GynecologicalInterviewAddEntryForm';
import { isPregnancyCompletionForm } from '../gynecologicalInterview/gynecologicalInterviewAddEntryForm/gynecologicalInterviewAddEntryForm.utils';
import { MedicalImagingEntryForm } from '../medicalImaging/MedicalImagingEntryForm';
import { MedicalImagingCreateAction } from '../medicalImaging/MedicalImagingEntryForm.actions';

import { MedicalInformationAddEntryForm } from './MedicalInformationAddEntryForm';
import {
   MedicalInformationAddEntryApproveDraftModalConfirmButton,
   MedicalInformationAddEntryModalExitButtons,
} from './MedicalInformationAddEntryModal.buttons';
import { MedicalInformationOnSuccessCallback } from './MedicalInformationAddEntryModal.types';

type MedicalInformationAddEntryModalContentProps = {
   readonly patientId: number;
   readonly medicalInformationType: MedicalInformationType;
   readonly medicalInformationId: number;
   readonly onClose: VoidFunction;
   readonly onSuccess: MedicalInformationOnSuccessCallback;
};

export const MedicalInformationAddEntryModalContent = (
   props: MedicalInformationAddEntryModalContentProps
): JSX.Element => {
   switch (props.medicalInformationType) {
      case 'GYNECOLOGICAL_INTERVIEW':
         return (
            <GynecologicalInterviewAddEntryForm medicalInformationId={props.medicalInformationId}>
               {({ handleSubmit, formState, interviewType }) => (
                  <>
                     {interviewType === 'PATIENT_PREGNANT' ? (
                        <GynecologicalInterviewActionsRenderer
                           medicalInformationId={props.medicalInformationId}
                           patientId={props.patientId}
                           onSuccess={props.onSuccess}
                           interviewType={interviewType}
                           handleSubmit={handleSubmit}
                        />
                     ) : null}
                     {interviewType === 'REGULAR_INTERVIEW' || interviewType === null ? (
                        <GynecologicalInterviewActionsRenderer
                           medicalInformationId={props.medicalInformationId}
                           patientId={props.patientId}
                           onSuccess={props.onSuccess}
                           interviewType={interviewType}
                           handleSubmit={handleSubmit}
                        />
                     ) : null}
                     {isPregnancyCompletionForm(interviewType, handleSubmit) ? (
                        <GynecologicalInterviewActionsRenderer
                           medicalInformationId={props.medicalInformationId}
                           patientId={props.patientId}
                           onSuccess={props.onSuccess}
                           interviewType={interviewType as PregnancyCompletionType}
                           handleSubmit={handleSubmit}
                        />
                     ) : null}
                     <MedicalInformationAddEntryModalExitButtons
                        shouldConfirmExit={formState.isDirty}
                        onClose={props.onClose}
                     />
                  </>
               )}
            </GynecologicalInterviewAddEntryForm>
         );
      case 'CHRONIC_DISEASE':
         return (
            <ChronicDiseaseEntryForm medicalInformationId={props.medicalInformationId}>
               {({ formState, handleSubmit }) => (
                  <>
                     <MedicalInformationAddEntryModalExitButtons
                        shouldConfirmExit={formState.isDirty}
                        onClose={props.onClose}
                     />
                     <ChronicDiseaseApproveDraftAction
                        medicalInformationId={props.medicalInformationId}
                        onSuccess={props.onSuccess}
                     >
                        {({ createChronicDisease }) => (
                           <ModalConfirmButton
                              testId={'createChronicDisease'}
                              label={patientMedicalInformationTranslationKeys.addEntryModal.addEntry}
                              onClick={handleSubmit(createChronicDisease)}
                           />
                        )}
                     </ChronicDiseaseApproveDraftAction>
                  </>
               )}
            </ChronicDiseaseEntryForm>
         );
      case 'MEDICAL_IMAGING':
         return (
            <MedicalImagingEntryForm medicalInformationId={props.medicalInformationId}>
               {({ formState, handleSubmit }) => (
                  <>
                     <MedicalInformationAddEntryModalExitButtons
                        shouldConfirmExit={formState.isDirty}
                        onClose={props.onClose}
                     />
                     <MedicalImagingCreateAction
                        handleSubmit={handleSubmit}
                        medicalInformationId={props.medicalInformationId}
                        onSuccess={props.onSuccess}
                     />
                  </>
               )}
            </MedicalImagingEntryForm>
         );
      default:
         return (
            <MedicalInformationAddEntryForm medicalInformationId={props.medicalInformationId}>
               {({ handleSubmit, formState }) => (
                  <>
                     <MedicalInformationAddEntryApproveDraftModalConfirmButton
                        handleSubmit={handleSubmit}
                        medicalInformationId={props.medicalInformationId}
                        medicalInformationType={props.medicalInformationType}
                        onSuccess={props.onSuccess}
                     />
                     <MedicalInformationAddEntryModalExitButtons
                        shouldConfirmExit={formState.isDirty}
                        onClose={props.onClose}
                     />
                  </>
               )}
            </MedicalInformationAddEntryForm>
         );
   }
};
